import { Dialog, DialogActions, DialogContent } from "@mui/material";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiDialogTitleClose from "components/MuiExtras/MuiDialogTitleClose";
import { GetSetupKnowledgeTestExceptionDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { SetupKnowledgeTestBaseFieldsComp } from "views/System/Components/SetupKnowledgeTestEdit";
import TopicAutoComplete from "views/Topic/Components/TopicAutoComplete";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    record: GetSetupKnowledgeTestExceptionDto;
    checkTopicExists?: (topidId: string) => boolean;
    onDelete?: (data: GetSetupKnowledgeTestExceptionDto) => void;
    onClose: (data: GetSetupKnowledgeTestExceptionDto) => void;
}

interface IState {
    record: GetSetupKnowledgeTestExceptionDto;
    confirmDelete: boolean;
}

type Props = IProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class SetupKnowledgeTestExceptionEditDialog extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            confirmDelete: false,
            record: props.record == null
                ? {
                    topicId: null,
                    topicName: null,
                    retentionDaysInterval: 30,
                    questionsBySession: 10,
                    sequential: false,
                    showCorrectAnswer: false
                }
                : props.record,
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.System.Components.SetupKnowledgeTestExceptionEditDialog." + r, f);
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        switch (name) {
            default:
                this.setState(state => {
                    const newRecord = { ...state.record };
                    newRecord[name] = v;
                    return { ...state, record: newRecord };
                });
        }
    }

    ///
    public render() {
        const { record, confirmDelete } = this.state;
        const { onClose, onDelete, checkTopicExists } = this.props;

        const selectedTopic: AutoCompleteDataModel[] =
            record.topicId == null
                ? []
                : [{
                    key: record.topicId,
                    label: record.topicName
                }];

        const canSave = record.topicId != null && record.questionsBySession > 0 && record.retentionDaysInterval > 0;

        return (
            <Dialog
                open
                fullWidth
                maxWidth="md"
            >

                <MuiDialogTitleClose
                    title={this.res(this.props.record == null ? "NewExceptionTitle" : "EditExceptionTitle")}
                    subtitle={this.props.record == null ? null : this.props.record.topicName}
                    onClose={() => this.props.onClose(null)}
                />

                <DialogContent>

                    {
                        this.props.record == null &&
                        <TopicAutoComplete
                            autoFocus
                            margin="normal"
                            multiple={false}
                            overModal
                            placeholder={this.props.t("Common.Literals.TopicSelection")}
                            selectedItems={selectedTopic}
                            onChange={
                                (topics) => {
                                    if (topics.length == 1) {
                                        if (checkTopicExists == null || !checkTopicExists(topics[0].key)) {
                                            this.setState(state => {
                                                const newRecord = { ...state.record };
                                                newRecord.topicId = topics[0].key;
                                                newRecord.topicName = topics[0].label;
                                                return { ...state, record: newRecord };
                                            });
                                        }
                                    }
                                }
                            }
                        />
                    }

                    <SetupKnowledgeTestBaseFieldsComp
                        autoFocus={this.props.record != null}
                        record={record}
                        handleChange={this.handleChange}
                    />

                    {
                        confirmDelete &&
                        <MuiConfirmation
                            message={this.res(".ConfirmationDeleteMessage")}
                            onClose={
                                (result) => {
                                    this.setState(
                                        { confirmDelete: false },
                                        () => { if (result) onDelete(this.props.record); }
                                    );
                                }
                            }
                        />
                    }

                </DialogContent>

                <DialogActions>

                    {
                        this.props.record == null
                            ? (
                                <MuiRegularButton
                                    onClick={() => this.props.onClose(null)}
                                >
                                    <span>{this.props.t("Common.Buttons.CancelText")}</span>
                                </MuiRegularButton>
                            )
                            : (
                                <MuiRegularButton
                                    color="error"
                                    onClick={() => this.setState({ confirmDelete: true })}
                                >
                                    <span>{this.props.t("Common.Buttons.DeleteText")}</span>
                                </MuiRegularButton>
                            )
                    }

                    <MuiRegularButton
                        color="success"
                        disabled={!canSave}
                        onClick={() => onClose(this.state.record)}
                    >
                        <span>{this.props.t("Common.Buttons.SaveText")}</span>
                    </MuiRegularButton>

                </DialogActions>
            </Dialog >

        );
    }
}

export default withTranslation()(SetupKnowledgeTestExceptionEditDialog);